// FILE: src/pages/info/ContactPage.jsx
// Public contact details and hours (MTO 12.3): the three support channels,
// the privacy contact, and the accommodation request before registration.
import React from "react";
import { Box, Button, Card, CardContent, Link, Stack, Typography } from "@mui/material";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import { Link as RouterLink } from "react-router-dom";
import InfoPage from "./InfoPage";
import { ChannelBlock } from "../../components/student/SupportChannelsCard";
import useAsync from "../../components/help/useAsync";
import { getPublicInfo } from "../../services/courseInfo";
import { ErrorState, LoadingState, Section } from "../../components/ui";

export default function ContactPage() {
  const { data, error, loading, reload } = useAsync(getPublicInfo, []);
  const channels = data?.supportChannels || [];
  return (
    <InfoPage
      title="Contact us"
      description="Questions before you register, or help during the course: here's who to contact and when we're available."
    >
      {loading && !data ? (
        <LoadingState variant="cards" rows={3} label="Loading contact details…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : (
        <Stack spacing={3}>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
            {channels.map((c) => (
              <ChannelBlock key={c.type} channel={c} />
            ))}
          </Box>
          <Card sx={{ bgcolor: "zun.primaryTint", borderColor: "transparent" }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
                <AccessibilityNewOutlinedIcon aria-hidden sx={{ color: "primary.main", fontSize: 36 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography component="h2" variant="h3">
                    Need an accommodation?
                  </Typography>
                  <Typography>
                    Tell us what would help before you register, and we'll work with you to make the course accessible.
                  </Typography>
                </Box>
                <Button component={RouterLink} to="/accommodation" variant="contained">
                  Request an accommodation
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Section id="company" title="About us">
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 650 }}>{data?.company?.name || "Zun Drivers"}</Typography>
              {data?.company?.mailingAddress && <Typography>{data.company.mailingAddress}</Typography>}
              {data?.privacyContact?.email && (
                <Typography>
                  Privacy questions: <Link href={`mailto:${data.privacyContact.email}`}>{data.privacyContact.email}</Link>
                </Typography>
              )}
              <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 1, pt: 1 }}>
                <Link component={RouterLink} to="/privacy">Privacy policy</Link>
                <Link component={RouterLink} to="/accessibility">Accessibility</Link>
                <Link component={RouterLink} to="/system-requirements">System requirements</Link>
                <Link component={RouterLink} to="/help">Help centre</Link>
              </Stack>
            </Stack>
          </Section>
        </Stack>
      )}
    </InfoPage>
  );
}
